import {useCallback, useEffect, useState} from "react";
import {Box} from "@unibuddy/patron";
import {useErrorReporting} from "@unibuddy/error-reporting";
import {SSOError as ISSOError, ssoHandleAuthCode} from "./auth-ui";
import {useAsyncLocalStorage} from "ubcommunity-shared/src/Auth/AuthProvider/useAsyncLocalStorage";
import {LocalStorageNames} from "ubcommunity-shared/src/constants";
import useAuth from "ubcommunity-shared/src/Auth/hooks/useAuth";
import ActivityIndicator from "ubcommunity-shared/src/General/ActivityIndicator/ActivityIndicator";
import {SSOError} from "./SSOError";

export type SSOHandleAuthProps = {
    code?: string;
};

export const SSOHandleAuth = ({code}: SSOHandleAuthProps) => {
    const {setAuth} = useAuth();
    const {value} = useAsyncLocalStorage(LocalStorageNames.SSO_FLOW, undefined);
    const [error, setError] = useState(undefined);
    const {reportError} = useErrorReporting();

    const onSuccess = useCallback(
        (bearerToken: string) => {
            setAuth({
                accessToken: bearerToken,
                tokenScheme: "Bearer",
                me: null,
            });
        },
        [setAuth],
    );

    const onError = useCallback(
        (e: ISSOError) => {
            //FIXME: Interim solution fixing error alert flashing before success
            setTimeout(() => {
                setError(e);
                reportError({
                    name: "SSOHandleAuthError",
                    message: e.error,
                });
            }, 2000);
        },
        [setError, reportError],
    );

    useEffect(() => {
        const handleAuth = async () => {
            await ssoHandleAuthCode({
                onSuccess,
                onError,
                // passing the context type as "university" does an additional check to verify its existence
                // https://github.com/Unibuddy/pokedex/blob/c85b26119dd878b2bacca4f3ed496b91fd9271e6/server/unibuddy_api/modules/userV2/application/jwt/logic.py#L114
                // here, we are basically stating that if the slug is "t" (generic), we want this to behave in a generic way
                // and bypass that check
                // i.e. base-url/u/t => bypass check
                // i.e. base-url/u/slug => check university existence
                contextType: value?.slug === "t" ? "community" : "university",
            });
        };
        handleAuth();
    }, [onError, onSuccess, value]);

    if (error) return <SSOError slug={value?.slug} />;

    return (
        <Box margin="xlarge">
            <ActivityIndicator accessibilityLabel="Logging in..." />
        </Box>
    );
};
